import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, MessageSquare, ArrowLeft, Loader2 } from 'lucide-react';

import { authService } from '../../services/api.js';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('email'); // 'email' | 'otp'
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'email') {
        await authService.forgotPassword(identifier);
        setSent(true);
      } else {
        await authService.forgotPasswordOtp(identifier);
        // Navigate to OTP verification page
        navigate('/verify-reset-otp', { state: { identifier } });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600">
            Choose how you want to reset your password.
          </p>
        </div>

        <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
          <button
            type="button"
            onClick={() => { setMode('email'); setSent(false); setError(''); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'email' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Mail className="w-4 h-4" />
            Email Link
          </button>
          <button
            type="button"
            onClick={() => { setMode('otp'); setSent(false); setError(''); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'otp' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Code
          </button>
        </div>

        {sent
          ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Check your email</h3>
              <p className="text-gray-600 mb-6">
              We sent a password reset link to {identifier}. Click the link to set a new password.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
              Back to login
              </Link>
            </div>
          )
          : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {mode === 'email' ? 'Email address' : 'Email or phone number'}
                </label>
                <input
                  type={mode === 'email' ? 'email' : 'text'}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={mode === 'email' ? 'you@example.com' : 'you@example.com or 0911...'}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  required
                />
                {mode === 'otp' && (
                  <p className="mt-2 text-xs text-gray-500">
                  Enter your email or Ethiopian phone number. We will send a 6-digit code.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !identifier}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === 'email' ? 'Send Reset Link' : 'Send Code'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                Back to login
                </Link>
              </div>
            </form>
          )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
